import HeaderTile from "../../components/desktop/HeaderTile";
import {Button, Checkbox, Col, Form, InputNumber, message, Row, Select, Tooltip} from "antd";
import React, {useEffect, useState} from "react";
import {HttpService} from "../../services/http/Http.service";
import {PlusOutlined} from "@ant-design/icons";
import {Simulator, StatisticsCombinator} from "../../model/models";
import {HttpErrorService} from "../../services/http/HttpError.service";
import CustomSpinner from "../../components/spinner/CustomSpinner";

export function DesktopPage() {
    const [form] = Form.useForm();
    const [filledFields, setFilledFields] = useState(0);

    const [combinator, setCombinator] = useState<Simulator[]>([])
    const [combinatorStatistics, setCombinatorStatistics] = useState<StatisticsCombinator[]>([])

    const [uploading, setUploading] = useState(false);

    const handleFilter = async () => {

        const values = form.getFieldsValue();
        const selectedYears = values.selectedYears
        const isOnlyCodesSelected = values.exemptionCodeChecked
        const selectedExemptionCodes = values.selectedCodes

        if (!selectedYears || selectedYears.length === 0) {
            message.error("Il simulatore non può essere creato senza anno.");
            return;
        }

        if (isOnlyCodesSelected && (!selectedExemptionCodes || selectedExemptionCodes.length === 0)) {
            message.error("Il simulatore non può essere creato senza specificare i codici esenzione.");
            return;
        }

        setUploading(true);

        const filteredEntry: Simulator = {
            filters: {
                numberOfPositions: values.quantity,
                exemptionCodes: values.selectedCodes,
                years: selectedYears,
                type: values.selectedType,
                minAmount: values.amountFrom,
                maxAmount: values.amountTo,
                checked: values.checked,
                exemptionCodeChecked: values.exemptionCodeChecked,
            }
        }
        try {
            const data = await HttpService.getInstanceAxios().post('/simulazione', filteredEntry)
            setCombinator([...combinator, data.data.simulator]);
            setCombinatorStatistics([...combinatorStatistics, data.data]);
            message.success("Combinatore creato con successo!");
        } catch (error) {
            HttpErrorService.handle("Errore durante la creazione del combinatore.", error, true)
        } finally {
            setUploading(false);
        }
    };

    const updateFieldCount = () => {
        const values = form.getFieldsValue();
        const filledCount = Object.values(values).filter(value => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === "number") return true;
            return value !== undefined && value !== "";
        }).length;

        setFilledFields(filledCount);
    };

    const handleDeleteCombinator = (id: number) => {
        setCombinatorStatistics(prev => prev.filter(item => item.simulator.id !== id));
    };

    useEffect(() => {
        const fetchCombinatorStatistics = async () => {
            try {
                const data = await HttpService.getInstanceAxios().get('/simulazione/statistiche/all')
                setCombinatorStatistics(
                    (data.data as StatisticsCombinator[]).sort(
                        (a: StatisticsCombinator, b: StatisticsCombinator) =>
                            new Date(a.simulator.createdAt!).getTime() - new Date(b.simulator.createdAt!).getTime()
                    )
                );

            } catch (error) {
                HttpErrorService.handle("Errore durante il recupero delle statistiche dei combinatori", error, true)
            }
        }
        fetchCombinatorStatistics()
    }, []);

    const [exemptionCodes, setExemptionCodes] = useState()
    useEffect(() => {
        const fetchExemptionCodes = async () => {
            try {
                const data = await HttpService.getInstanceAxios().get('/exemption/list')
                setExemptionCodes(data.data.map((code: string) => ({
                    label: code,
                    value: code,
                })))
            } catch (error) {
                HttpErrorService.handle("Errore durante il recupero dei codici esenzione", error, true)
            }
        }
        fetchExemptionCodes()
    }, []);

    const [years, setYears] = useState()
    useEffect(() => {
        const fetchYears = async () => {
            try {
                const data = await HttpService.getInstanceAxios().get('/file-ts/get-combinator-years')
                setYears(data.data.map((year: string) => ({
                    label: year,
                    value: year,
                })))
            } catch (error) {
                HttpErrorService.handle("Errore durante il recupero degli anni", error, true)
            }
        }
        fetchYears()
    }, []);

    const isDisabled = filledFields < 2;

    return (
        <>
            <Form form={form} layout="vertical" onValuesChange={updateFieldCount}>
                <Row gutter={16} align="bottom">
                    <Col>
                        <Form.Item name="quantity" label="N° Assistiti">
                            <InputNumber min={1}/>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="selectedCodes" label="Codice Esenzione">
                            <Select mode="multiple" allowClear placeholder="Codice/i"
                                    options={exemptionCodes}/>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item
                            name="exemptionCodeChecked"
                            valuePropName="checked"
                        >
                            <Checkbox>
                                Solo Codici Indicati
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={5}>
                        <Form.Item name="selectedYears" label="Anno">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Anno/i"
                                options={years}/>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item
                            name="checked"
                            valuePropName="checked"
                        >
                            <Checkbox>
                                Solo Anni Indicati
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="amountFrom" label="Importo" initialValue={20}>
                            <InputNumber placeholder="Da"/>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="amountTo">
                            <InputNumber placeholder="A"/>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item>
                            <Tooltip title={isDisabled ? "Compila almeno due campi per attivare il filtro" : ""}>
                                <Button
                                    icon={<PlusOutlined/>}
                                    type="primary"
                                    onClick={handleFilter}
                                    disabled={isDisabled}
                                >
                                </Button>
                            </Tooltip>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Row style={{marginTop: 20, justifyContent: 'center', gap: 20}}>
                {combinatorStatistics.map((item) =>
                    <HeaderTile
                        id={item.simulator.id}
                        title={
                            "Combinatore " + item.simulator.filters.years.join(" - ") + " " +
                            (
                                item.simulator.filters.checked && item.simulator.filters.exemptionCodeChecked
                                    ? "(Anni/Codici indicati)"
                                    : item.simulator.filters.checked
                                        ? "(Anni indicati)"
                                        : item.simulator.filters.exemptionCodeChecked
                                            ? "(Codici indicati)"
                                            : ""
                            )
                        }
                        numberAssisted={item.statistics.totalPerson}
                        numberPositions={item.statistics.totalOccurrences}
                        codes={item.simulator.filters.exemptionCodes}
                        typeList={item.statistics.typeList}
                        amount={[item.simulator.filters.minAmount, item.simulator.filters.maxAmount]}
                        totalTicket={item.statistics.totalTicket}
                        totalCost={item.statistics.totalCosts}
                        total={item.statistics.totalSum}
                        totalSanction={item.statistics.totalSanction}
                        totalInterests={item.statistics.totalInterests}
                        key={item.simulator.id}
                        createdAt={item.simulator.createdAt}
                        onDelete={handleDeleteCombinator}
                    ></HeaderTile>
                )}
            </Row>
            <CustomSpinner
                message={"Creazione combinatore in corso..."}
                open={uploading}
            />
        </>
    )
}